import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  EVENT_TYPE_LABEL_HU,
  SOURCE_CLIENT_LABEL_HU,
  type EventView,
} from '@persped/shared';
import { Link as RouterLink } from 'react-router-dom';
import { formatTimestamp } from '../format';

/**
 * Eseménynapló-táblázat — a mappa-, raklap- és globális nézet ugyanezt
 * használja, mert a spec 12.1 §-a szerint EGY közös eseményforrás van, és a
 * történeti nézetek ennek szűrt lekérdezései.
 *
 * Két KÖTELEZŐ szabály van beépítve:
 *
 * 1. A sikertelen események vizuálisan elkülönülnek (piros, ikonnal és
 *    magyar jelöléssel). A spec 12. §-a szó szerint kimondja, hogy a
 *    sikertelen műveletek „nem jelenhetnek meg úgy, mintha üzletileg
 *    végrehajtódtak volna" — egy egységes szürke sor pont ezt tenné.
 *
 * 2. NINCS szerkesztés és NINCS törlés. Sem gomb, sem menü, sem inline
 *    szerkesztés. A napló nem módosítható normál felhasználói felületről
 *    (spec 12. §, US-FOLDER-ACTION-3 AC4) — ezért itt szándékosan nincs
 *    olyan `onEdit`/`onDelete` prop, amit egy jövőbeli képernyő átadhatna.
 */
export function EventTable({
  events,
  showEntityColumns = true,
  emptyText = 'Még nincs esemény.',
}: {
  events: EventView[];
  /** Egyetlen mappa/raklap előzményénél a saját vonalkód-oszlop redundáns. */
  showEntityColumns?: boolean;
  emptyText?: string;
}) {
  return (
    <TableContainer sx={{ maxHeight: 620 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Időpont</TableCell>
            <TableCell>Esemény</TableCell>
            {showEntityColumns && <TableCell>Mappa</TableCell>}
            {showEntityColumns && <TableCell>Raklap</TableCell>}
            <TableCell>Honnan → Hova</TableCell>
            <TableCell>Ki</TableCell>
            <TableCell>Kliens</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {events.length === 0 && (
            <TableRow>
              <TableCell colSpan={showEntityColumns ? 7 : 5}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {events.map((e) => {
            const failed = e.result === 'FAILURE';
            return (
              <TableRow
                key={e.id}
                // Halvány piros háttér: a sor első pillantásra elkülönül,
                // de a szöveg olvasható marad.
                sx={failed ? { backgroundColor: 'rgba(211, 47, 47, 0.07)' } : undefined}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTimestamp(e.occurredAt)}</TableCell>

                <TableCell>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {failed && (
                      <Tooltip title="A művelet SIKERTELEN volt — üzletileg nem hajtódott végre.">
                        <ErrorOutlinedIcon color="error" fontSize="small" />
                      </Tooltip>
                    )}
                    <Typography variant="body2" color={failed ? 'error.main' : undefined}>
                      {EVENT_TYPE_LABEL_HU[e.eventType]}
                    </Typography>
                    {failed && (
                      <Chip size="small" color="error" variant="outlined" label="Sikertelen" />
                    )}
                  </Stack>
                </TableCell>

                {showEntityColumns && (
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {e.folderBarcode ? (
                      <RouterLink to={`/mappak/${e.folderBarcode}`}>{e.folderBarcode}</RouterLink>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                )}

                {showEntityColumns && (
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    {e.palletBarcode ? (
                      <RouterLink to={`/lokaciok/${e.palletBarcode}`}>{e.palletBarcode}</RouterLink>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                )}

                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {formatMove(e)}
                </TableCell>

                <TableCell>{e.userDisplayName ?? e.userId}</TableCell>
                {/*
                  A KÉSZÜLÉK a forrás mellett (2026-08-14-i CR #18): több TC22
                  mellett a „Zebra" önmagában nem mond semmit. A becenév a
                  Beállításokban átírható; ha nincs, az azonosító rövid alakja.
                */}
                <TableCell>
                  {SOURCE_CLIENT_LABEL_HU[e.sourceClient]}
                  {(e.deviceName ?? e.deviceId) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {e.deviceName ?? e.deviceId?.slice(0, 8)}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** „LOC-001/2 → RKL-003/1" alakban, csak a kitöltött részekből. */
function formatMove(e: EventView): string {
  const from = withLevel(e.sourceLocationBarcode, e.sourceLevel);
  const to = withLevel(e.targetLocationBarcode, e.targetLevel);
  if (!from && !to) return '—';
  if (!from) return `→ ${to}`;
  if (!to) return `${from} →`;
  return `${from} → ${to}`;
}

function withLevel(barcode: string | null, level: number | null): string {
  if (!barcode) return '';
  return level !== null ? `${barcode}/${level}` : barcode;
}
